import { useEffect, useState } from 'react';
import { Alert, Badge, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import emailPreviewService from '@/shared/services/emailPreviewService';

function EmailPreviewsTab() {
    const [events, setEvents] = useState([]);
    const [eventKey, setEventKey] = useState('');
    const [role, setRole] = useState('');
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let active = true;
        emailPreviewService
            .listEvents()
            .then(({ events: list }) => {
                if (!active) return;
                setEvents(list);
                if (list.length > 0) {
                    setEventKey(list[0].key);
                    setRole(list[0].roles[0]);
                }
            })
            .catch(() => {
                if (active) setError('Could not load the email events.');
            })
            .finally(() => {
                if (active) setLoading(false);
            });
        return () => {
            active = false;
        };
    }, []);

    useEffect(() => {
        if (!eventKey || !role) return undefined;
        let active = true;
        setError(null);
        emailPreviewService
            .getPreview(eventKey, role)
            .then((data) => {
                if (active) setPreview(data);
            })
            .catch(() => {
                if (active) {
                    setPreview(null);
                    setError('Could not render that email.');
                }
            });
        return () => {
            active = false;
        };
    }, [eventKey, role]);

    const selected = events.find((e) => e.key === eventKey);

    const handleEventChange = (e) => {
        const next = events.find((ev) => ev.key === e.target.value);
        setEventKey(next.key);
        setRole(next.roles[0]);
    };

    if (loading) {
        return (
            <div className="text-center py-4">
                <Spinner animation="border" size="sm" role="status">
                    <span className="visually-hidden">
                        Loading email events...
                    </span>
                </Spinner>
            </div>
        );
    }

    return (
        <Card className="border-0 shadow-sm">
            <Card.Header className="bg-transparent">
                <h2 className="h5 mb-0">Email Previews</h2>
            </Card.Header>
            <Card.Body>
                <p className="text-muted">
                    Each email as the recipient would receive it, built from
                    sample data. Nothing is sent from this page.
                </p>
                {error && <Alert variant="danger">{error}</Alert>}
                {events.length === 0 && !error && (
                    <p className="text-muted mb-0">No email events defined.</p>
                )}
                {selected && (
                    <>
                        <Row className="g-3 mb-3">
                            <Col md={6}>
                                <Form.Group controlId="email-preview-event">
                                    <Form.Label>Event</Form.Label>
                                    <Form.Select
                                        value={eventKey}
                                        onChange={handleEventChange}
                                    >
                                        {events.map((e) => (
                                            <option key={e.key} value={e.key}>
                                                {e.key}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group controlId="email-preview-role">
                                    <Form.Label>Recipient role</Form.Label>
                                    <Form.Select
                                        value={role}
                                        onChange={(e) =>
                                            setRole(e.target.value)
                                        }
                                    >
                                        {selected.roles.map((r) => (
                                            <option key={r} value={r}>
                                                {r}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={2} className="d-flex align-items-end">
                                <Badge
                                    bg={
                                        selected.required ? 'dark' : 'secondary'
                                    }
                                >
                                    {selected.required
                                        ? 'Required'
                                        : 'Optional'}
                                </Badge>
                            </Col>
                        </Row>
                        {preview && (
                            <>
                                <h3 className="h6">Subject</h3>
                                <p>{preview.subject}</p>
                                <h3 className="h6">HTML version</h3>
                                <iframe
                                    title={`HTML preview of ${preview.event}`}
                                    sandbox=""
                                    srcDoc={preview.html}
                                    className="email-preview-frame w-100 border rounded mb-3"
                                />
                                <h3 className="h6">Plain-text version</h3>
                                <pre className="email-preview-text border rounded p-3 bg-light">
                                    {preview.text}
                                </pre>
                            </>
                        )}
                    </>
                )}
            </Card.Body>
        </Card>
    );
}

export default EmailPreviewsTab;
